import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useUserContext } from '../context/UserContext';
import './Header.css';
import { LoginModalProvider } from '../context/LoginModalContext';
import LoginModal from './login/LoginModal';
import { useNotifications } from '../context/NotificationContext';
import FilterBar from './FilterBar';
import { FilterOptions } from './FilterBar';
import { useAnalytics } from '../context/AnalyticsContext';

const SSR_APP_URL = process.env.REACT_APP_SSR_APP_URL || 'https://aika.camp';

interface HeaderProps {
  className?: string;
  onFiltersChange?: (filters: FilterOptions) => void;
  onClearAll?: () => void;
  showFilterBar?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onFiltersChange,
  onClearAll,
  showFilterBar = false
}) => {
  const { user, isLoggedIn, logout } = useUserContext();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
	const { showSuccess } = useNotifications();
  const { trackEvent } = useAnalytics();

	const handleRegister = (email: string) => {
    setShowLoginModal(false);
		showSuccess("You have successfully registered!")
	};

	const handleLogin = (email: string) => {
    setShowLoginModal(false);
		showSuccess("You have successfully logged in!")
	};  


  const getUserInitials = (name?: string) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLoginClose = () => {
    setShowLoginModal(false);
  };

  const handleLogout = () => {
    logout();
    setShowUserDropdown(false);
    trackEvent('header_logout_link_clicked', { location: window.location.pathname });
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showUserDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.header-user-menu')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Scroll detection for filter bar visibility (only on screens >= 1400px)
  useEffect(() => {
    if (!showFilterBar) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const screenWidth = window.innerWidth;
      
      // Only enable scroll behavior on screens >= 1400px
      if (screenWidth >= 1400) {
        setIsScrolled(scrollTop > 100);
      } else {
        setIsScrolled(false);
      }
    };

    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1400) {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [showFilterBar]);

  return (
    <>
      <header className="app-header">
        <div className="header-container">
          <div className="header-logo">
            <a href={SSR_APP_URL} className="logo-link">
              <img src="/images/aika-logo.svg" alt="Camp Planner Logo" className="logo-image" />
            </a>
          </div>
          
          {showFilterBar && onFiltersChange && onClearAll && (
            <div className={`header-filter-bar ${isScrolled ? 'visible' : ''}`}>
              <FilterBar 
                onFiltersChange={onFiltersChange}
                onClearAll={onClearAll}
              />
            </div>
          )}

          <div className="header-right">
            <nav className="header-nav">
              <ul className="nav-links">
                <li>
                  <a
                    href={`${SSR_APP_URL}/search`}
                    className="nav-link"
                    onClick={() => trackEvent('header_navigation_clicked', { link: 'Find Camps', location: window.location.pathname })}
                  >
                    Find Camps
                  </a>
                </li>
                <li className="nav-link-desktop-only">
                  <a
                    href={`${SSR_APP_URL}/favorites`}
                    className="nav-link"
                    onClick={() => trackEvent('header_navigation_clicked', { link: 'Favorites', location: window.location.pathname })}
                  >
                    Favorites
                  </a>
                </li>
                <li>
                  <a
                    href={`${SSR_APP_URL}/schedule`}
                    className="nav-link"
                    onClick={() => trackEvent('header_navigation_clicked', { link: 'Schedule', location: window.location.pathname })}
                  >
                    Schedule
                  </a>
                </li>
              </ul>
            </nav>

            <div className="header-user">
              {isLoggedIn && user ? (
                <div className="header-user-menu">
                  <button
                    onClick={handleLogout}
                    className="logout-button logout-button-desktop"
                    aria-label="Log out"
                  >
                    Log Out
                  </button>
                  <Link
                    to="/profile"
                    className="user-avatar"
                    title={`${user.name} - Profile`}
                    onClick={() => trackEvent('header_navigation_clicked', { link: 'Profile', location: window.location.pathname })}
                  >
                    {getUserInitials(user.name)}
                  </Link>
                  <button
                    onClick={toggleUserDropdown}
                    className="menu-button"
                    aria-label="Open menu"
                    aria-expanded={showUserDropdown}
                    aria-haspopup="true"
                  >
                    <Menu className="icon icon--sm" />
                  </button>

                  {showUserDropdown && (
                    <div className="user-dropdown">
                      <a
                        href={`${SSR_APP_URL}/favorites`}
                        className="user-dropdown-item"
                        onClick={() => {
                          setShowUserDropdown(false);
                          trackEvent('header_navigation_clicked', { link: 'Favorites', location: window.location.pathname });
                        }}
                      >
                        Favorites
                      </a>
                      <Link
                        to="/profile"
                        className="user-dropdown-item"
                        onClick={() => {
                          setShowUserDropdown(false);
                          trackEvent('header_navigation_clicked', { link: 'Profile', location: window.location.pathname });
                        }}
                      >
                        Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="user-dropdown-item user-dropdown-logout"
                      >
                        Log Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    trackEvent('header_login_link_clicked' , { location: window.location.pathname });
                  }}
                  className="nav-link header-login-link"
                >
                  Log In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Render LoginModal when needed */}
      {showLoginModal && (
				<LoginModalProvider initialStep="email">
          <LoginModal onClose={handleLoginClose} onLogin={handleLogin} onRegister={handleRegister} />
        </LoginModalProvider>
      )}
    </>
  );
};

export default Header;
